import Button from 'components/Form/Button';
import BasicTable from 'components/Form/Table';
import TableWrapper from 'components/TableWrapper';
import ContainerAuthenticated from 'containers/Authenticated';
import { CoreContext } from 'context/CoreContext';
import { useCallback, useContext, useEffect, useMemo, useState } from 'react';
import { useHistory } from 'react-router-dom';
import { Read } from 'services/financing-proposals';
import { StatusContent, Title } from 'ui/styled';
import { exposeStrapiError, marcadorDiferente, selectColors } from 'utils';
import { parseCurrency } from 'utils/parsers';
import { formatadores, normalizeName } from 'utils/validators';

export default function FinancingDeskRecusadas() {
  const history = useHistory();
  const navigate = useCallback((to) => history.push(`/${to}`), [history]);
  const { user, setModal, filterExpression } = useContext(CoreContext);
  const [loading, setLoading] = useState(false);
  const [registers, setRegisters] = useState([]);
  // Define table columns
  const columns = useMemo(() => {
    const baseColumns = [
      {
        title: 'Id',
        sortable: true,
        ref: 'id',
        renderCell: ({ row }) => {
          // Verifica se foi analisado pela curadoria humana ou pelo bot
          const isAnalisadoCuradoria = row?.curadoria_analise === 'Analisado';
          const isAnalisadoBot = row?.curadoria_analise_bot === 'Analisado';
          const id = row?.id;
          if (isAnalisadoCuradoria || isAnalisadoBot) {
            return marcadorDiferente(id, '!');
          }
          return id;
        },
      },
      {
        title: 'Data',
        sortable: true,
        ref: 'date',
        renderCell: ({ row }) => formatadores.TIMESTAMP_TO_BR(row.date),
      },
      { title: 'Hora', ref: 'hora' },
      { title: 'Proposta', ref: 'cod_proposta_externa' },
      // Inserir coluna "Distribuidora" condicionalmente
      ...(user?.administrador ||
      (user?.distribuidor && user?.distribuidores?.length > 0)
        ? [{ title: 'Distribuidora', sortable: true, ref: 'distributor' }]
        : []),
      { title: 'Integrador', sortable: true, ref: 'integrator' },
      { title: 'Cliente', sortable: true, ref: 'customer' },
      { title: 'Valor', ref: 'value' },
      {
        title: 'Status',
        ref: 'status',
        renderCell: ({ row }) => (
          <StatusContent
            color={selectColors(row?.status)}
            style={{ cursor: 'pointer' }}
            onClick={() => {
              if (user?.administrador) {
                setModal({
                  type: 'recusado',
                  dados: row,
                  reload: () => init(),
                });
              }
            }}
          >
            {row?.status}
          </StatusContent>
        ),
      },
      // Inserir coluna "Espelho da Proposta" condicionalmente
      ...(user?.administrador
        ? [
            {
              title: 'Espelho da Proposta',
              ref: 'espelho',
              renderCell: ({ row }) => (
                <div style={{ display: 'flex', justifyContent: 'center' }}>
                  <Button
                    onClick={() => navigate(`proposals/financing/${row?.id}`)}
                    color="blue"
                    nospace
                    mbutton
                    small
                  >
                    Visualizar
                  </Button>
                </div>
              ),
            },
          ]
        : []),
    ].filter((f) => f);
    return baseColumns;
  }, [user, navigate, setModal]);
  // Map registers to table rows
  const rows = useMemo(() => {
    return registers?.map((item) => ({
      id: item?.id,
      date: item?.createdAt,
      formatedDate: formatadores.TIMESTAMP_TO_BR(item?.createdAt),
      hora: formatadores.TIMESTAMP_TO_TIME(item?.createdAt),
      cod_proposta_externa: normalizeName(item?.cod_proposta_externa),
      distributor: normalizeName(item?.distribuidor?.trade_name),
      integrator: normalizeName(item?.integrador?.nome_fantasia),
      customer: normalizeName(item?.informacoes_cliente?.nome_completo),
      value: parseCurrency(item?.informacoes_projeto?.custo_total_projeto),
      status: normalizeName(item?.status),
      status_obs: item?.status_obs,
      motivos_recusa: item?.motivos_recusa,
      motivos_recusa_fatura: item?.motivos_recusa_fatura,
      motivos_reprovado: !item?.banking?.motivos_reprovado
        ? null
        : item?.banking?.motivos_reprovado,
      cnpj_integrador: item?.integrador?.cnpj,
      cnpj_integrador_clean: item?.integrador?.cnpj.replace(/\D/g, ''),
      cpf_cnpj: item?.cliente?.cpf || item?.cliente?.cnpj,
      cpf_cnpj_clean: item?.cliente?.cpf
        ? item?.cliente?.cpf.replace(/\D/g, '')
        : item?.cliente?.cnpj.replace(/\D/g, ''),
      curadoria_analise: item?.curadoria_analise,
    }));
  }, [registers]);
  // Initialize data
  const init = async () => {
    setLoading(true);
    const hoje = new Date();
    const dataLimite = new Date();
    dataLimite.setDate(hoje.getDate() - 30);
    const dataISO = dataLimite.toISOString();
    const result = await Read(
      `?filters[status]=recusado&filters[createdAt][$gte]=${dataISO}&populate=*`
    );
    setLoading(false);
    if (result && !exposeStrapiError(result)) {
      const nresult = result?.results?.sort((a, b) => b.id - a.id);
      const nextResult = nresult?.map((m) => ({
        ...m,
        accepted: m?.propostas_banco?.find((f) => f.aceito),
      }));
      setRegisters(nextResult);
    }
  };

  useEffect(() => init(), []);

  return (
    <ContainerAuthenticated>
      <Title>Propostas</Title>
      <TableWrapper title={'Inelegíveis'} search>
        <BasicTable
          loading={loading}
          columns={columns}
          rows={rows?.filter(filterExpression)}
          pagination
        />
      </TableWrapper>
    </ContainerAuthenticated>
  );
}
